import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, X, Check, ArrowLeftRight } from 'lucide-react';

export default function CalendarFilterModal({
  isOpen,
  onClose,
  type = 'stops', // 'stops' or 'missions' or 'expedition'
  title: customTitle,
  subtitle: customSubtitle,
  currentFilter = { startDate: '', endDate: '', isAll: false },
  onApplyFilter
}) {
  const today = new Date();
  const formatIsoDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const todayStr = formatIsoDate(today);

  const isInitialRange = !!(currentFilter.startDate && currentFilter.endDate && currentFilter.startDate !== currentFilter.endDate);
  const [selectionMode, setSelectionMode] = useState(isInitialRange ? 'range' : 'single');

  const [viewYear, setViewYear] = useState(() => {
    if (currentFilter.startDate) {
      const parts = currentFilter.startDate.split('-');
      if (parts.length === 3) return parseInt(parts[0], 10);
    }
    return today.getFullYear();
  });

  const [viewMonth, setViewMonth] = useState(() => {
    if (currentFilter.startDate) {
      const parts = currentFilter.startDate.split('-');
      if (parts.length === 3) return parseInt(parts[1], 10) - 1;
    }
    return today.getMonth();
  });

  // Selection states:
  const [selectedStart, setSelectedStart] = useState(currentFilter.startDate || todayStr);
  const [selectedEnd, setSelectedEnd] = useState(currentFilter.endDate || todayStr);
  const [isAllSelected, setIsAllSelected] = useState(!!currentFilter.isAll);
  const [hoverDate, setHoverDate] = useState(null);
  const [isSelectingRange, setIsSelectingRange] = useState(false);

  // Calendar counts state:
  const [calendarCounts, setCalendarCounts] = useState({ stops: {}, missions: {} });
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  // Sync with currentFilter when opened
  useEffect(() => {
    if (isOpen) {
      const isRange = !!(currentFilter.startDate && currentFilter.endDate && currentFilter.startDate !== currentFilter.endDate);
      setSelectionMode(isRange ? 'range' : 'single');
      setSelectedStart(currentFilter.startDate || todayStr);
      setSelectedEnd(currentFilter.endDate || todayStr);
      setIsAllSelected(!!currentFilter.isAll);
      setIsSelectingRange(false);
      
      if (currentFilter.startDate) {
        const parts = currentFilter.startDate.split('-');
        if (parts.length === 3) {
          setViewYear(parseInt(parts[0], 10));
          setViewMonth(parseInt(parts[1], 10) - 1);
        }
      }
    }
  }, [isOpen, currentFilter]);

  // Fetch counts when viewYear or viewMonth changes
  useEffect(() => {
    if (!isOpen) return;
    const fetchCounts = async () => {
      setIsLoadingCounts(true);
      try {
        const monthStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
        const res = await fetch(`/api/database/calendar-counts?month=${monthStr}`);
        if (res.ok) {
          const data = await res.json();
          setCalendarCounts({
            stops: data.stops || {},
            missions: data.missions || {}
          });
        }
      } catch (err) {
        console.error('Error loading calendar counts:', err);
      }
      setIsLoadingCounts(false);
    };

    fetchCounts();
  }, [isOpen, viewYear, viewMonth]);

  if (!isOpen) return null;

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const weekDayHeaders = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const handlePrevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(y => y - 1);
    } else {
      setViewMonth(m => m - 1);
    }
  };

  const handleNextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(y => y + 1);
    } else {
      setViewMonth(m => m + 1);
    }
  };

  const handleGoToToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedStart(todayStr);
    setSelectedEnd(todayStr);
    setIsAllSelected(false);
    setIsSelectingRange(false);
    setSelectionMode('single');
  };

  // Build calendar matrix
  const firstDayOfMonth = new Date(viewYear, viewMonth, 1);
  const lastDayOfMonth = new Date(viewYear, viewMonth + 1, 0);
  const totalDays = lastDayOfMonth.getDate();

  // Day of week: 0 is Sun, 1 is Mon. We want 0=Mon, ..., 6=Sun
  let startDayOfWeek = firstDayOfMonth.getDay() - 1;
  if (startDayOfWeek === -1) startDayOfWeek = 6;

  const daysGrid = [];
  for (let i = 0; i < startDayOfWeek; i++) {
    daysGrid.push(null);
  }
  for (let day = 1; day <= totalDays; day++) {
    const dStr = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    daysGrid.push({
      day,
      dateStr: dStr
    });
  }

  const countsMap = type === 'missions' ? calendarCounts.missions : calendarCounts.stops;

  // Handle Day Click
  const handleDayClick = (dateStr) => {
    setIsAllSelected(false);
    if (selectionMode === 'single') {
      setSelectedStart(dateStr);
      setSelectedEnd(dateStr);
      setIsSelectingRange(false);
    } else {
      if (!isSelectingRange) {
        // First click in range mode: sets start date, waits for end date
        setSelectedStart(dateStr);
        setSelectedEnd(dateStr);
        setIsSelectingRange(true);
      } else {
        // Second click in range mode: completes range
        if (dateStr < selectedStart) {
          setSelectedEnd(selectedStart);
          setSelectedStart(dateStr);
        } else {
          setSelectedEnd(dateStr);
        }
        setIsSelectingRange(false);
      }
    }
  };

  // Handle Day Double Click (instant apply single date)
  const handleDayDoubleClick = (dateStr) => {
    setIsAllSelected(false);
    setSelectedStart(dateStr);
    setSelectedEnd(dateStr);
    setIsSelectingRange(false);
    setSelectionMode('single');
    if (onApplyFilter) {
      onApplyFilter({
        startDate: dateStr,
        endDate: dateStr,
        isAll: false
      });
    }
    onClose();
  };

  // Quick Preset Handlers
  const handlePresetToday = () => {
    setSelectedStart(todayStr);
    setSelectedEnd(todayStr);
    setIsAllSelected(false);
    setIsSelectingRange(false);
    setSelectionMode('single');
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  };

  const handlePresetThisWeek = () => {
    const now = new Date();
    const day = now.getDay();
    const diffToMon = (day === 0 ? -6 : 1) - day;
    const mon = new Date(now);
    mon.setDate(now.getDate() + diffToMon);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);

    const monStr = formatIsoDate(mon);
    const sunStr = formatIsoDate(sun);

    setSelectedStart(monStr);
    setSelectedEnd(sunStr);
    setIsAllSelected(false);
    setIsSelectingRange(false);
    setSelectionMode('range');
    setViewYear(mon.getFullYear());
    setViewMonth(mon.getMonth());
  };

  const handlePresetThisMonth = () => {
    const now = new Date();
    const startM = new Date(now.getFullYear(), now.getMonth(), 1);
    const endM = new Date(now.getFullYear(), now.getMonth() + 1, 0);

    setSelectedStart(formatIsoDate(startM));
    setSelectedEnd(formatIsoDate(endM));
    setIsAllSelected(false);
    setIsSelectingRange(false);
    setSelectionMode('range');
    setViewYear(now.getFullYear());
    setViewMonth(now.getMonth());
  };

  const handlePresetAll = () => {
    setIsAllSelected(true);
    setIsSelectingRange(false);
  };

  const handleApply = () => {
    if (isAllSelected) {
      onApplyFilter({ startDate: null, endDate: null, isAll: true });
    } else {
      onApplyFilter({
        startDate: selectedStart || todayStr,
        endDate: selectedEnd || selectedStart || todayStr,
        isAll: false
      });
    }
    onClose();
  };

  const calcDaysDiff = (startStr, endStr) => {
    if (!startStr || !endStr) return 1;
    const [sy, sm, sd] = startStr.split('-').map(Number);
    const [ey, em, ed] = endStr.split('-').map(Number);
    const d1 = new Date(sy, sm - 1, sd);
    const d2 = new Date(ey, em - 1, ed);
    const diff = Math.round(Math.abs((d2 - d1) / (1000 * 60 * 60 * 24))) + 1;
    return diff || 1;
  };

  // Check if date is within selected range
  const isDateSelected = (dateStr) => {
    if (isAllSelected) return false;
    if (isSelectingRange && hoverDate) {
      const minD = selectedStart < hoverDate ? selectedStart : hoverDate;
      const maxD = selectedStart < hoverDate ? hoverDate : selectedStart;
      return dateStr >= minD && dateStr <= maxD;
    }
    const minD = selectedStart <= selectedEnd ? selectedStart : selectedEnd;
    const maxD = selectedStart <= selectedEnd ? selectedEnd : selectedStart;
    return dateStr >= minD && dateStr <= maxD;
  };

  const isRangeStart = (dateStr) => !isAllSelected && dateStr === selectedStart;
  const isRangeEnd = (dateStr) => !isAllSelected && dateStr === selectedEnd;

  // Format selection summary
  const formatReadableDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(10, 15, 29, 0.82)',
        backdropFilter: 'blur(8px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: '16px',
          width: '620px',
          maxWidth: '95vw',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7), 0 0 25px rgba(56, 189, 248, 0.15)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* MODAL HEADER */}
        <div
          style={{
            padding: '16px 20px',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '10px',
                background: type === 'missions' ? 'rgba(245, 158, 11, 0.15)' : 'rgba(56, 189, 248, 0.15)',
                border: `1px solid ${type === 'missions' ? '#f59e0b' : '#38bdf8'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: type === 'missions' ? '#f59e0b' : '#38bdf8'
              }}
            >
              <Calendar size={18} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '800', color: '#f8fafc' }}>
                {customTitle || (type === 'expedition' ? "Date d'expédition" : (type === 'missions' ? 'Filtrer les Missions par date' : 'Filtrer les Stops par date'))}
              </h3>
              <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
                {customSubtitle || (type === 'expedition' ? 'Sélectionnez un jour ou une plage de dates de livraison' : 'Sélectionnez un jour ou une plage de dates pour afficher les données')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#64748b',
              cursor: 'pointer',
              padding: '6px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* MODE SELECTOR & GUIDANCE BANNER */}
        <div style={{ padding: '10px 20px', background: '#090d16', borderBottom: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
            {/* Mode Switcher Tabs */}
            <div style={{ display: 'flex', background: '#1e293b', borderRadius: '8px', padding: '3px', border: '1px solid #334155' }}>
              <button
                type="button"
                onClick={() => {
                  setSelectionMode('single');
                  setSelectedEnd(selectedStart);
                  setIsSelectingRange(false);
                  setIsAllSelected(false);
                }}
                style={{
                  background: selectionMode === 'single' && !isAllSelected ? '#38bdf8' : 'transparent',
                  color: selectionMode === 'single' && !isAllSelected ? '#0f172a' : '#94a3b8',
                  border: 'none',
                  padding: '5px 12px',
                  borderRadius: '6px',
                  fontSize: '0.78rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease'
                }}
              >
                <Calendar size={13} />
                <span>Date unique</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setSelectionMode('range');
                  setIsSelectingRange(false);
                  setIsAllSelected(false);
                }}
                style={{
                  background: selectionMode === 'range' && !isAllSelected ? '#38bdf8' : 'transparent',
                  color: selectionMode === 'range' && !isAllSelected ? '#0f172a' : '#94a3b8',
                  border: 'none',
                  padding: '5px 12px',
                  borderRadius: '6px',
                  fontSize: '0.78rem',
                  fontWeight: '800',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.15s ease'
                }}
              >
                <ArrowLeftRight size={13} />
                <span>Plage de dates</span>
              </button>
            </div>

            {/* Live Selection Indicator Badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '700' }}>Actuel :</span>
              <span style={{
                background: isAllSelected ? 'rgba(34, 197, 94, 0.15)' : (selectedStart === selectedEnd ? 'rgba(56, 189, 248, 0.15)' : 'rgba(245, 158, 11, 0.15)'),
                color: isAllSelected ? '#22c55e' : (selectedStart === selectedEnd ? '#38bdf8' : '#f59e0b'),
                border: `1px solid ${isAllSelected ? 'rgba(34, 197, 94, 0.4)' : (selectedStart === selectedEnd ? 'rgba(56, 189, 248, 0.4)' : 'rgba(245, 158, 11, 0.4)')}`,
                padding: '3px 8px',
                borderRadius: '6px',
                fontSize: '0.75rem',
                fontWeight: '800'
              }}>
                {isAllSelected
                  ? 'Toutes les dates'
                  : selectedStart === selectedEnd
                    ? formatReadableDate(selectedStart)
                    : `${formatReadableDate(selectedStart)} ➜ ${formatReadableDate(selectedEnd)} (${calcDaysDiff(selectedStart, selectedEnd)}j)`}
              </span>
            </div>
          </div>

          {/* Interactive Guidance Tip */}
          <div style={{
            fontSize: '0.72rem',
            color: '#cbd5e1',
            background: 'rgba(30, 41, 59, 0.6)',
            padding: '6px 12px',
            borderRadius: '6px',
            border: '1px solid #334155',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>
              {isAllSelected
                ? '🌐 Mode sans filtre : affiche toutes les livraisons et missions.'
                : selectionMode === 'single'
                  ? '👉 Cliquez sur un jour pour le sélectionner, ou double-cliquez pour appliquer immédiatement.'
                  : isSelectingRange
                    ? `👉 Étape 2 : Cliquez sur la date de fin (Début sélectionné : ${formatReadableDate(selectedStart)})`
                    : selectedStart !== selectedEnd
                      ? `✅ Plage prête : du ${formatReadableDate(selectedStart)} au ${formatReadableDate(selectedEnd)}. Cliquez sur un jour pour changer le début.`
                      : '👉 Étape 1 : Cliquez sur la date de début de la plage.'}
            </span>
            {selectionMode === 'range' && selectedStart !== selectedEnd && !isAllSelected && (
              <span style={{ color: '#22c55e', fontWeight: '800', marginLeft: '8px', whiteSpace: 'nowrap' }}>
                {calcDaysDiff(selectedStart, selectedEnd)} jours
              </span>
            )}
          </div>
        </div>

        {/* PRESET SHORTCUTS BAR */}
        <div
          style={{
            padding: '8px 20px',
            background: '#090d16',
            borderBottom: '1px solid #1e293b',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flexWrap: 'wrap'
          }}
        >
          <span style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: '700', marginRight: '4px' }}>
            Raccourcis :
          </span>
          <button
            type="button"
            onClick={handlePresetToday}
            style={{
              background: !isAllSelected && selectedStart === todayStr && selectedEnd === todayStr ? '#38bdf8' : '#1e293b',
              color: !isAllSelected && selectedStart === todayStr && selectedEnd === todayStr ? '#0f172a' : '#cbd5e1',
              border: '1px solid #334155',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            Aujourd'hui
          </button>
          <button
            type="button"
            onClick={handlePresetThisWeek}
            style={{
              background: '#1e293b',
              color: '#cbd5e1',
              border: '1px solid #334155',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            Cette semaine
          </button>
          <button
            type="button"
            onClick={handlePresetThisMonth}
            style={{
              background: '#1e293b',
              color: '#cbd5e1',
              border: '1px solid #334155',
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer'
            }}
          >
            Ce mois
          </button>
          <button
            type="button"
            onClick={handlePresetAll}
            style={{
              background: isAllSelected ? '#22c55e' : '#1e293b',
              color: isAllSelected ? '#0f172a' : '#cbd5e1',
              border: `1px solid ${isAllSelected ? '#22c55e' : '#334155'}`,
              padding: '4px 10px',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: '700',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          >
            Tout afficher (Sans filtre)
          </button>
        </div>

        {/* CALENDAR BODY */}
        <div style={{ padding: '16px 20px', flex: 1, overflowY: 'auto' }}>
          {/* Month / Year Navigator */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: '16px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '1.05rem', fontWeight: '800', color: '#f8fafc' }}>
                {monthNames[viewMonth]} {viewYear}
              </span>
              {isLoadingCounts && (
                <span style={{ fontSize: '0.72rem', color: '#38bdf8' }}>Chargement...</span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                onClick={handleGoToToday}
                style={{
                  background: 'rgba(56, 189, 248, 0.1)',
                  color: '#38bdf8',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                Mois courant
              </button>
              <button
                type="button"
                onClick={handlePrevMonth}
                style={{
                  background: '#1e293b',
                  color: '#f8fafc',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  padding: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronLeft size={16} />
              </button>
              <button
                type="button"
                onClick={handleNextMonth}
                style={{
                  background: '#1e293b',
                  color: '#f8fafc',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  padding: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>

          {/* Weekday Headers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '6px',
              marginBottom: '8px',
              textAlign: 'center'
            }}
          >
            {weekDayHeaders.map((header, idx) => (
              <div
                key={idx}
                style={{
                  fontSize: '0.72rem',
                  fontWeight: '700',
                  color: idx >= 5 ? '#ef4444' : '#64748b',
                  padding: '4px 0'
                }}
              >
                {header}
              </div>
            ))}
          </div>

          {/* Days Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: '6px'
            }}
          >
            {daysGrid.map((item, idx) => {
              if (!item) {
                return (
                  <div
                    key={`empty-${idx}`}
                    style={{
                      height: '62px',
                      background: 'rgba(15, 23, 42, 0.4)',
                      borderRadius: '8px'
                    }}
                  />
                );
              }

              const { day, dateStr } = item;
              const count = countsMap[dateStr] || 0;
              const isToday = dateStr === todayStr;
              const isSelected = isDateSelected(dateStr);
              const isStart = !isAllSelected && dateStr === selectedStart;
              const isEnd = !isAllSelected && dateStr === selectedEnd;
              const isSingle = !isAllSelected && selectedStart === selectedEnd && isStart;
              const isBetween = !isAllSelected && isSelected && !isStart && !isEnd;

              let bg = '#1e293b';
              let borderColor = '#334155';
              let textColor = '#f8fafc';

              if (isBetween) {
                bg = type === 'missions' ? 'rgba(245, 158, 11, 0.22)' : 'rgba(56, 189, 248, 0.22)';
                borderColor = type === 'missions' ? 'rgba(245, 158, 11, 0.6)' : 'rgba(56, 189, 248, 0.6)';
                textColor = '#ffffff';
              }
              if (isStart || isEnd) {
                bg = type === 'missions' ? '#f59e0b' : '#38bdf8';
                borderColor = type === 'missions' ? '#d97706' : '#0284c7';
                textColor = '#0f172a';
              }

              return (
                <div
                  key={dateStr}
                  onClick={() => handleDayClick(dateStr)}
                  onDoubleClick={() => handleDayDoubleClick(dateStr)}
                  onMouseEnter={() => isSelectingRange && setHoverDate(dateStr)}
                  title={selectionMode === 'single' ? 'Cliquer ou double-cliquer pour choisir ce jour' : 'Cliquer pour définir le début ou la fin de la plage'}
                  style={{
                    height: '62px',
                    background: bg,
                    border: `1px solid ${borderColor}`,
                    borderRadius: '8px',
                    padding: '5px 6px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    position: 'relative',
                    transition: 'all 0.15s ease',
                    boxShadow: isStart || isEnd ? '0 0 10px rgba(56, 189, 248, 0.4)' : 'none'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span
                      style={{
                        fontSize: '0.86rem',
                        fontWeight: isToday || isSelected || isStart || isEnd ? '800' : '600',
                        color: textColor
                      }}
                    >
                      {day}
                    </span>

                    {/* Distinct Status Badges on Cell */}
                    {isSingle && (
                      <span
                        style={{
                          fontSize: '0.56rem',
                          background: '#0f172a',
                          color: '#38bdf8',
                          padding: '1px 5px',
                          borderRadius: '4px',
                          fontWeight: '800'
                        }}
                      >
                        Choisi
                      </span>
                    )}

                    {!isSingle && isStart && (
                      <span
                        style={{
                          fontSize: '0.56rem',
                          background: '#0f172a',
                          color: '#38bdf8',
                          padding: '1px 5px',
                          borderRadius: '4px',
                          fontWeight: '800'
                        }}
                      >
                        Début
                      </span>
                    )}

                    {!isSingle && isEnd && (
                      <span
                        style={{
                          fontSize: '0.56rem',
                          background: '#0f172a',
                          color: '#38bdf8',
                          padding: '1px 5px',
                          borderRadius: '4px',
                          fontWeight: '800'
                        }}
                      >
                        Fin
                      </span>
                    )}

                    {isToday && !isStart && !isEnd && (
                      <span
                        style={{
                          fontSize: '0.58rem',
                          background: 'rgba(56, 189, 248, 0.18)',
                          color: '#38bdf8',
                          padding: '1px 4px',
                          borderRadius: '4px',
                          fontWeight: '800'
                        }}
                      >
                        Auj.
                      </span>
                    )}
                  </div>

                  {/* Day Count Badge */}
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {count > 0 ? (
                      <span
                        style={{
                          fontSize: '0.68rem',
                          fontWeight: '800',
                          padding: '2px 6px',
                          borderRadius: '6px',
                          background: isStart || isEnd
                            ? 'rgba(15, 23, 42, 0.3)'
                            : (type === 'missions' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(34, 197, 94, 0.2)'),
                          color: isStart || isEnd
                            ? '#0f172a'
                            : (type === 'missions' ? '#f59e0b' : '#22c55e'),
                          border: isStart || isEnd
                            ? 'none'
                            : `1px solid ${type === 'missions' ? 'rgba(245, 158, 11, 0.4)' : 'rgba(34, 197, 94, 0.4)'}`,
                          display: 'inline-block',
                          textAlign: 'center',
                          width: '100%',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {count} {type === 'missions' ? 'm.' : 'st.'}
                      </span>
                    ) : (
                      <span
                        style={{
                          fontSize: '0.65rem',
                          color: isStart || isEnd ? 'rgba(15, 23, 42, 0.6)' : '#475569',
                          fontWeight: '500'
                        }}
                      >
                        0 {type === 'missions' ? 'mis.' : 'stop'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* MODAL FOOTER */}
        <div
          style={{
            padding: '14px 20px',
            borderTop: '1px solid #1e293b',
            background: '#090d16',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: '700', textTransform: 'uppercase' }}>
                Filtre sélectionné :
              </span>
              <span style={{
                fontSize: '0.66rem',
                fontWeight: '800',
                padding: '1px 6px',
                borderRadius: '4px',
                background: isAllSelected ? 'rgba(34, 197, 94, 0.2)' : (selectedStart === selectedEnd ? 'rgba(56, 189, 248, 0.2)' : 'rgba(245, 158, 11, 0.2)'),
                color: isAllSelected ? '#22c55e' : (selectedStart === selectedEnd ? '#38bdf8' : '#f59e0b'),
                border: `1px solid ${isAllSelected ? 'rgba(34, 197, 94, 0.4)' : (selectedStart === selectedEnd ? 'rgba(56, 189, 248, 0.4)' : 'rgba(245, 158, 11, 0.4)')}`
              }}>
                {isAllSelected ? 'TOUT AFFICHER' : (selectedStart === selectedEnd ? 'DATE UNIQUE' : `PLAGE : ${calcDaysDiff(selectedStart, selectedEnd)} JOURS`)}
              </span>
            </div>
            <span style={{ fontSize: '0.86rem', fontWeight: '800', color: '#f8fafc' }}>
              {isAllSelected ? (
                'Toutes les dates sans restriction'
              ) : selectedStart === selectedEnd ? (
                `Journée du ${formatReadableDate(selectedStart)}`
              ) : (
                `Du ${formatReadableDate(selectedStart)} au ${formatReadableDate(selectedEnd)} (${calcDaysDiff(selectedStart, selectedEnd)} jours)`
              )}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: '#1e293b',
                color: '#94a3b8',
                border: '1px solid #334155',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>

            <button
              type="button"
              onClick={handleApply}
              style={{
                background: type === 'missions' ? '#f59e0b' : '#22c55e',
                color: '#0f172a',
                border: 'none',
                padding: '8px 18px',
                borderRadius: '8px',
                fontSize: '0.8rem',
                fontWeight: '800',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: type === 'missions' ? '0 0 12px rgba(245, 158, 11, 0.3)' : '0 0 12px rgba(34, 197, 94, 0.3)'
              }}
            >
              <Check size={15} strokeWidth={3} />
              <span>Appliquer</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
